import { forwardRef, type ComponentPropsWithoutRef, type MouseEvent, type FocusEvent } from 'react';

import { Link } from 'react-router-dom';

import { usePrefetch } from '@/hooks/usePrefetch';

type LinkProps = ComponentPropsWithoutRef<typeof Link>;

export interface PrefetchLinkProps extends LinkProps {
  /**
   * The destination path. Will be used for both navigation and prefetching.
   */
  to: string;
}

/**
 * Link component that prefetches route chunks on hover/focus.
 * Wraps react-router-dom's Link with prefetch functionality.
 * Forwards refs for compatibility with Radix UI primitives (e.g., Tooltip).
 *
 * @example
 * ```tsx
 * <PrefetchLink to="/marketplace/applications" className="nav-link">
 *   Marketplace
 * </PrefetchLink>
 * ```
 *
 * @see GW-223: Route prefetching on hover/focus
 */
export const PrefetchLink = forwardRef<HTMLAnchorElement, PrefetchLinkProps>(function PrefetchLink(
  { to, children, onMouseEnter, onFocus, ...rest },
  ref
) {
  const { prefetch } = usePrefetch();

  const handleMouseEnter = (e: MouseEvent<HTMLAnchorElement>) => {
    prefetch(to);
    onMouseEnter?.(e);
  };

  const handleFocus = (e: FocusEvent<HTMLAnchorElement>) => {
    prefetch(to);
    onFocus?.(e);
  };

  return (
    <Link ref={ref} to={to} onMouseEnter={handleMouseEnter} onFocus={handleFocus} {...rest}>
      {children}
    </Link>
  );
});
